import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';

import useAuthenticatedRequest from '../../utils/TokenRefreshUtils';
import useValidateShift from '../../utils/ShiftUtils';
import { formatShortDate } from '../../utils/CommonUtils';

import Card from '../common/Card';
import Drawer from '../common/Drawer';
import EmptyState from '../common/EmptyState';
import Money from '../common/Money';
import PaymentEditor, { UndistributedMessage } from '../common/PaymentEditor';
import SummaryList, { SummaryRow } from '../common/SummaryList';

/**
 * Повернення товару клієнтові.
 *
 * Виправлено в цій версії:
 *  • кнопка «Зберегти і вийти» викликала порожню функцію saveDraft() —
 *    прибрана, бо чернетки повернення бекенд не підтримує;
 *  • кнопка «Видалити» рендерилася за прапорцем isEdit, якого в цьому
 *    екрані ніколи не було;
 *  • у платіж підставлявся `values.client`, якого немає в формі, тому
 *    повернення завжди створювалося без клієнта — тепер береться клієнт акту;
 *  • сума до повернення рахувалася як «сума + знижка» (знижка додавалася
 *    замість віднімання) — тепер береться готова сума до оплати з акту.
 */
const SaleReturn = () => {
    const authenticatedRequest = useAuthenticatedRequest();
    const validateShift = useValidateShift();
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useRef(null);

    const act = location.state?.actData ?? null;

    const [currencyRates, setCurrencyRates] = useState(null);
    const [payments, setPayments] = useState([]);
    const [paymentErrors, setPaymentErrors] = useState([]);
    const [paymentVisible, setPaymentVisible] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!act) {
            return;
        }

        setPayments(
            (act.count_amount ?? []).map((item) => ({
                currency: item.currency,
                sum: Number(item.amount) || 0,
                paymentMethod: null,
            }))
        );
    }, [act]);

    useEffect(() => {
        const fetchRates = async () => {
            const response = await authenticatedRequest(
                `${process.env.REACT_APP_BACKEND_URL}currency/get_currencies_list/`,
                { method: 'GET', headers: { 'Content-Type': 'application/json' } }
            );

            if (response?.status === 200) {
                const parsed = await response.json();
                setCurrencyRates(
                    parsed.reduce((acc, item) => {
                        acc[item.currency_short] = item.currency_rate;
                        return acc;
                    }, {})
                );
            }
        };

        fetchRates();
    }, [authenticatedRequest]);

    /** Сума до повернення в гривні — вже з урахуванням знижки акту. */
    const totalReturnUAH = useMemo(
        () =>
            (act?.count_amount ?? []).reduce(
                (sum, item) => sum + (Number(item.amountUAH) || 0),
                0
            ),
        [act]
    );

    const undistributed = useMemo(() => {
        if (!currencyRates) {
            return totalReturnUAH;
        }

        const covered = payments.reduce(
            (sum, payment) => sum + Number(payment.sum) * (currencyRates[payment.currency] ?? 0),
            0
        );

        return totalReturnUAH - covered;
    }, [payments, currencyRates, totalReturnUAH]);

    const discounts = (act?.discount ?? []).filter((item) => Number(item.amount) > 0);

    const buildReturnRequest = () => {
        const withReturnLink = (items = []) =>
            items.map((item) => ({ ...item, return_sale_act_id: act.id }));

        return {
            cagent: act.cagent ?? null,
            division: act.division,
            act_date: formatShortDate(new Date()),
            pay_due_date: formatShortDate(new Date()),
            act_number_autogenerate: true,
            shift_id: Cookies.get('ShiftId'),
            unidentified_products: withReturnLink(act.unidentified_products),
            identified_products: withReturnLink(act.identified_products),
        };
    };

    const submitReturn = async () => {
        const errors = payments.map((payment) =>
            payment.paymentMethod ? {} : { paymentMethod: 'Вкажіть спосіб повернення коштів' }
        );

        setPaymentErrors(errors);

        if (!payments.length || errors.some((error) => error.paymentMethod)) {
            return;
        }

        setBusy(true);

        try {
            if (!(await validateShift())) {
                navigate('/shift', {
                    state: {
                        toast: {
                            severity: 'error',
                            summary: 'Зміна закрита',
                            detail: 'Повернення неможливе',
                            life: 4000,
                        },
                    },
                });
                return;
            }

            const createResponse = await authenticatedRequest(
                `${process.env.REACT_APP_BACKEND_URL}client_return_acts/create/`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(buildReturnRequest()),
                }
            );

            if (createResponse?.status !== 201) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Не вдалося створити акт повернення',
                    detail: `Код помилки: ${createResponse?.status ?? 'невідомо'}`,
                    life: 4000,
                });
                return;
            }

            const { id: returnId } = await createResponse.json();

            const finalizeResponse = await authenticatedRequest(
                `${process.env.REACT_APP_BACKEND_URL}client_return_acts/finalize/${returnId}/`,
                { method: 'GET', headers: { 'Content-Type': 'application/json' } }
            );

            if (finalizeResponse?.status !== 200) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Помилка фіналізації акту повернення',
                    detail: `Код помилки: ${finalizeResponse?.status ?? 'невідомо'}`,
                    life: 4000,
                });
                return;
            }

            const paymentRequest = payments.map((payment) => ({
                act_id: returnId,
                date: formatShortDate(Date.now()),
                sum: payment.sum,
                currency: payment.currency,
                type: payment.paymentMethod,
                method: 'OUTCOME',
                cagent_id: act.cagent ?? null,
                shift_id: Cookies.get('ShiftId'),
            }));

            const paymentResponse = await authenticatedRequest(
                `${process.env.REACT_APP_BACKEND_URL}accounting_payments/create/`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(paymentRequest),
                }
            );

            if (paymentResponse?.status === 201) {
                navigate('/shift', {
                    state: {
                        toast: {
                            severity: 'success',
                            summary: 'Повернення оформлено',
                            detail: `Акт повернення №${returnId}`,
                            life: 3000,
                        },
                    },
                });
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Помилка створення платежу',
                    detail: `Код помилки: ${paymentResponse?.status ?? 'невідомо'}`,
                    life: 4000,
                });
            }
        } finally {
            setBusy(false);
        }
    };

    const confirmReturn = () => {
        confirmDialog({
            header: 'Підтвердження повернення',
            message: `Повернути клієнту кошти за актом №${act.id}? Дію не можна скасувати.`,
            icon: 'pi pi-replay',
            acceptLabel: 'Оформити повернення',
            rejectLabel: 'Скасувати',
            acceptClassName: 'p-button-danger',
            accept: submitReturn,
        });
    };

    if (!act) {
        return (
            <EmptyState
                icon="pi pi-exclamation-triangle"
                title="Немає даних для повернення"
                text="Відкрийте акт продажу в історії та натисніть «Оформити повернення»"
                action={
                    <Button
                        label="До історії"
                        icon="pi pi-arrow-left"
                        text
                        onClick={() => navigate('/salehistory')}
                    />
                }
            />
        );
    }

    return (
        <>
            <Toast ref={toast} position="top-center" />

            <div className="rc-stack">
                <div>
                    <h1 className="rc-page-title">Повернення за актом №{act.id}</h1>
                    <p className="rc-page-subtitle">
                        {act.cagentName ?? 'Клієнта не зазначено'}
                    </p>
                </div>

                <Message
                    className="rc-message"
                    severity="warn"
                    text="Повернення виводить кошти з каси і не може бути скасоване"
                />

                <Card title="Сума до повернення" flush>
                    <SummaryList>
                        {(act.count_amount ?? []).map((item) => (
                            <SummaryRow key={`return-${item.currency}`} label={item.currency}>
                                <Money
                                    value={item.amount}
                                    currency={item.currency}
                                    tone="outcome"
                                />
                            </SummaryRow>
                        ))}
                        {discounts.map((item) => (
                            <SummaryRow
                                key={`discount-${item.currency}`}
                                label={`Врахована знижка, ${item.currency}`}
                            >
                                <Money value={item.amount} currency={item.currency} />
                            </SummaryRow>
                        ))}
                        <SummaryRow label="Разом" sublabel="еквівалент у гривні" total>
                            <Money value={totalReturnUAH} currency="UAH" tone="outcome" />
                        </SummaryRow>
                    </SummaryList>
                </Card>

                <div className="rc-stack rc-stack--tight">
                    <Button
                        label="Зафіксувати повернення"
                        icon="pi pi-replay"
                        severity="danger"
                        className="w-full"
                        onClick={() => setPaymentVisible(true)}
                    />
                    <Button
                        label="Назад"
                        icon="pi pi-arrow-left"
                        text
                        className="w-full"
                        onClick={() => navigate(-1)}
                    />
                </div>
            </div>

            <Drawer
                visible={paymentVisible}
                onHide={() => setPaymentVisible(false)}
                title="Спосіб повернення коштів"
                footer={
                    <Button
                        label="Оформити повернення"
                        icon="pi pi-check"
                        severity="danger"
                        className="w-full"
                        loading={busy}
                        disabled={!payments.length}
                        onClick={confirmReturn}
                    />
                }
            >
                <UndistributedMessage amount={undistributed} />
                <PaymentEditor
                    payments={payments}
                    onChange={setPayments}
                    errors={paymentErrors}
                />
            </Drawer>
        </>
    );
};

export default SaleReturn;
